import { ampli } from '@/ampli';
import { useConnectWallet, usePrivy } from '@privy-io/react-auth';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import type { Chain } from 'viem';

import { useFeature } from '@/hooks/useFeature';
import { useKeyPress } from '@/hooks/useKeypress';
import { usePaidFeedbackEligibility } from '@/hooks/usePaidFeedbackEligibility';

import { resetCheckoutSelector, useTradeStore } from '@/store/trade';
import { recentTransactionSelector, useTransactionStore } from '@/store/transaction';

import { NotificationIndicatorIcon } from '@/ui/Icons/NotificationIndicatorIcon';
import { SearchIcon } from '@/ui/Icons/SearchIcon';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { USER_FEEDBACK } from '@/utils/featureFlags';
import { getDefaultTradeParams } from '@/utils/swap';

import { NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID } from '../../constants/chain';
import { useMatchaWallets } from '../../hooks/useMatchaWallets';
import { buttons } from '../../styles/buttons';
import { fontStyles } from '../../styles/typography';
import { SEARCH_TYPES } from '../../utils/amplitude';
import { classNames } from '../../utils/classnames';
// utils
import { routes } from '../../utils/routes';
import AdditionalOptionsDropdown from './AdditionalOptionsDropdown';
import { FeedbackTooltip } from './FeedbackTooltip';
import { HeaderBase } from './HeaderBase';
import { FeatureFlagIndicator, HeaderConnectButton } from './HeaderButtons';
import MatchaLogoButton from './MatchaLogoButton';
import { PaidFeedbackTooltip } from './PaidFeedbackTooltip';

const TokenSelectModal = dynamic(() => import('../TransactionModule/TokenSelectModal'), {
  ssr: false,
});

/**
 * Props for the Header component
 */
interface HeaderProps {
  /** Whether to show or hide the global search bar. */
  hideSearch?: boolean;
  isHomepage?: boolean;
  /** When provided, filters the center search bar to these chains. */
  searchChains?: Chain[];
  /** Override wallet chain type for the connect button (e.g. 'solana-only' on Solana page). */
  walletChainType?: 'ethereum-and-solana' | 'ethereum-only' | 'solana-only';
}

/**
 * Landing page hero content.
 * @param props - HeaderProps
 */
export const Header = ({
  hideSearch = false,
  isHomepage = false,
  searchChains,
  walletChainType,
}: HeaderProps) => {
  const router = useRouter();
  const hasSlashBeenPressed = useKeyPress('/');
  const resetCheckout = useTradeStore(resetCheckoutSelector);
  const isUserFeedbackEnabled = useFeature(USER_FEEDBACK);
  const { recentTransaction } = useTransactionStore(recentTransactionSelector);

  const handleConnectionSuccess = () => {
    const target = redirectOnConnectionSuccess.current;
    if (target) {
      redirectOnConnectionSuccess.current = null;
      router.push(target);
    }
  };

  const { isConnected, solanaWallet, ethereumWallet, connect } = useMatchaWallets();
  // Register a separate onSuccess for the redirect side effect; Privy fans out
  // useConnectWallet callbacks across all consumers that register one.
  useConnectWallet({ onSuccess: handleConnectionSuccess });
  const { isModalOpen, ready } = usePrivy();
  const { data: paidFeedbackEligibility } = usePaidFeedbackEligibility(ethereumWallet?.address);

  const [showFeedbackHint, setShowFeedbackHint] = useState(false);
  const [showPaidFeedbackHint, setShowPaidFeedbackHint] = useState(false);
  const [mounted, setMounted] = useState(false);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const redirectOnConnectionSuccess = useRef<
    typeof routes.TRADE_HISTORY | typeof routes.PORTFOLIO | null
  >(null);

  // Listen for feedback hint changes from other components
  useEffect(() => {
    const handleFeedbackHintChange = (event: CustomEvent<boolean>) => {
      const shouldShow = event.detail && isConnected && isUserFeedbackEnabled;
      setShowFeedbackHint(shouldShow);
    };

    window.addEventListener('feedbackHintChanged', handleFeedbackHintChange as EventListener);
    return () => {
      window.removeEventListener('feedbackHintChanged', handleFeedbackHintChange as EventListener);
    };
  }, [isConnected, isUserFeedbackEnabled]);

  // Listen for paid feedback hint changes from other components
  useEffect(() => {
    const handlePaidFeedbackHintChange = (event: CustomEvent<boolean>) => {
      const isEligible =
        paidFeedbackEligibility?.isEligible && !paidFeedbackEligibility?.hasSubmitted;
      const shouldShow = event.detail && isConnected && isEligible;
      setShowPaidFeedbackHint(!!shouldShow);
    };

    window.addEventListener(
      'paidFeedbackHintChanged',
      handlePaidFeedbackHintChange as EventListener,
    );
    return () => {
      window.removeEventListener(
        'paidFeedbackHintChanged',
        handlePaidFeedbackHintChange as EventListener,
      );
    };
  }, [isConnected, paidFeedbackEligibility?.isEligible, paidFeedbackEligibility?.hasSubmitted]);

  useEffect(() => {
    if (!hasSlashBeenPressed) return;
    const element = document.activeElement;

    const shouldFocus =
      !(element instanceof HTMLInputElement) &&
      !(element instanceof HTMLTextAreaElement) &&
      !(
        element instanceof HTMLElement &&
        (element.contentEditable === 'true' || element.contentEditable === '')
      );
    if (!shouldFocus) return;

    // set timeout so focus happens after the slash key event has bubbled
    // so '/' isn't typed into the input field.
    setTimeout(() => buttonRef.current?.click(), 0);
  }, [hasSlashBeenPressed]);

  const handleHistoryClick = (event: React.MouseEvent) => {
    ampli.navHistoryClicked();

    if (!ready) return;
    if (!isConnected) {
      event.preventDefault();
      connect();
      redirectOnConnectionSuccess.current = routes.TRADE_HISTORY;
    }
  };

  const handlePortfolioClick = (event: React.MouseEvent) => {
    if (!ready) return;
    if (!isConnected) {
      event.preventDefault();
      connect();
      redirectOnConnectionSuccess.current = routes.PORTFOLIO;
    }
  };

  const handleSelectTokenResult = async (token: TokenResult) => {
    await router.push({
      pathname: routes.TOKEN_PROFILE,
      query: {
        chainId: NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[token.chainId],
        contractAddresses: [token.address],
      },
    });
    resetCheckout();
  };

  useEffect(() => setMounted(true), []);

  // Do not redirect on wallet connection success when user does not complete
  // the wallet connection after clicking on the history link
  useEffect(() => {
    if (!isModalOpen && !isConnected && redirectOnConnectionSuccess.current) {
      redirectOnConnectionSuccess.current = null;
    }
  }, [isModalOpen, isConnected]);

  // event tracking
  const searchType = isHomepage ? SEARCH_TYPES.HOME_PAGE : SEARCH_TYPES.HEADER;

  const tradePageQuery = getDefaultTradeParams(ethereumWallet, solanaWallet);
  const tradePage = { pathname: routes.SWAP, query: tradePageQuery };

  return (
    <HeaderBase data-hide-search={hideSearch} data-connected={isConnected}>
      <nav className="flex items-center gap-6 grow">
        <MatchaLogoButton />
        <div className="flex-row hidden header-sm:flex">
          <Link
            href={routes.INDEX}
            className={buttons({ size: 'small', shape: 'pill', color: 'transparent' })}
            onClick={() => ampli.navDiscoverClicked()}
          >
            Discover
          </Link>
          <Link
            href={tradePage}
            className={buttons({ size: 'small', shape: 'pill', color: 'transparent' })}
            onClick={() => ampli.navTradeClicked()}
          >
            Trade
          </Link>
          <Link
            href={routes.PORTFOLIO}
            className={buttons({ size: 'small', shape: 'pill', color: 'transparent' })}
            onClick={handlePortfolioClick}
          >
            Portfolio
          </Link>
          <Link
            href={routes.TRADE_HISTORY}
            className={buttons({ size: 'small', shape: 'pill', color: 'transparent' })}
            onClick={handleHistoryClick}
          >
            History
            {mounted && recentTransaction && isConnected && (
              <NotificationIndicatorIcon className="ml-1.5" />
            )}
          </Link>
        </div>
      </nav>
      <div className="flex flex-row items-center justify-end grow gap-2">
        <TokenSelectModal
          onSelect={handleSelectTokenResult}
          searchType={searchType}
          isHomepage={isHomepage}
          supportedChains={searchChains}
        >
          <button
            className={fontStyles({
              variant: 'BODY_MD',
              class: [
                'group-data-[hide-search=false]:[transition:visibility_0s,opacity_200ms,background_200ms]',
                'group-data-[hide-search=true]:[transition:visibility_200ms,opacity_200ms,background_200ms]',
                'group-data-[hide-search=true]:invisible group-data-[hide-search=true]:opacity-0',
                'hidden header-md:block visible opacity-100 bg-token-alphalight-300 rounded-2xl border-0 text-350',
                'h-11 overflow-hidden text-ellipsis px-10 relative whitespace-nowrap z-10 backdrop-blur-[5px]',
                'w-full header-lg:max-w-[318px] header-md:max-w-[228px] hover:bg-token-alphalight-400',
                'focus-visible:outline-[3px] focus-visible:outline-token-gray-600 focus-visible:outline-solid',
              ],
            })}
            ref={buttonRef}
            onClick={() => ampli.searchFocused({ search_type: searchType })}
          >
            <span className="text-token-gray-800">Search tokens</span>
            <SearchIcon className="text-token-gray-1600 h-4 w-4 pointer-none absolute top-3.25 left-4 z-10" />
            <span
              className={classNames(
                'text-token-gray-800 bg-token-alphalight-300 border-token-alphalight-300',
                'flex size-6 items-center justify-center rounded-xs border border-solid',
                'absolute right-4 top-[9px] z-10 pointer-events-none',
              )}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.9368 2.70532L7.88991 14.0249H5.96484L9.01172 2.70532H10.9368Z"
                  fill="currentColor"
                />
              </svg>
            </span>
          </button>
        </TokenSelectModal>

        <TokenSelectModal
          onSelect={handleSelectTokenResult}
          searchType={isHomepage ? SEARCH_TYPES.HOME_PAGE : SEARCH_TYPES.HEADER}
        >
          <div
            className={buttons({
              size: 'medium',
              color: 'white',
              shape: 'pill',
              iconOnly: true,
              class: [
                'group-data-[hide-search=true]:hidden group-data-[hide-search=false]:block',
                'group-data-[hide-search=false]:header-md:hidden size-10 items-center justify-center p-3',
              ],
            })}
          >
            <SearchIcon className="size-4" />
            <VisuallyHidden>Search</VisuallyHidden>
          </div>
        </TokenSelectModal>

        <FeatureFlagIndicator />
        <div data-backpack-confetti-anchor>
          <HeaderConnectButton walletChainType={walletChainType} />
        </div>

        <div className="relative">
          <AdditionalOptionsDropdown
            onOpen={() => {
              setShowFeedbackHint(false);
              setShowPaidFeedbackHint(false);
            }}
            showPulsingDot={showFeedbackHint || showPaidFeedbackHint}
            onTradeHistoryClick={handleHistoryClick}
            onPortfolioClick={handlePortfolioClick}
            tradePage={tradePage}
          />
          {showFeedbackHint && <FeedbackTooltip onDismiss={() => setShowFeedbackHint(false)} />}
          {showPaidFeedbackHint && (
            <PaidFeedbackTooltip onDismiss={() => setShowPaidFeedbackHint(false)} />
          )}
        </div>
      </div>
    </HeaderBase>
  );
};
